import { BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { useCallback, useEffect, useMemo } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { Text, View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'
import { useDeepCompareMemoize } from 'use-deep-compare-effect'

import AdvancedFilterRow from '@/components/filtering/advanced/AdvancedFilterRow'
import {
  AdvancedFilterFormValues,
  AdvancedFilterSchema,
} from '@/components/filtering/advanced/AdvancedFilterTypes'
import TourSpot from '@/components/tour/TourSpot'
import UniButton from '@/components/unistyles/UniButton'
import UniDivider from '@/components/unistyles/UniDivider'
import useAutoSave from '@/hooks/useAutoSave'
import { useAppStore } from '@/store/useAppStore'

function setNestedValue(obj: any, path: string[], value: any) {
  let current = obj
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    if (!(key in current)) {
      current[key] = {}
    } else if (typeof current[key] !== 'object' || current[key] === null) {
      current[key] = {}
    }
    current = current[key]
  }
  current[path[path.length - 1]] = value
}

function flattenFilters(
  filters: { [key: string]: any },
  prefix = ''
): { field: string; operator: string; value: any }[] {
  let result: { field: string; operator: string; value: any }[] = []
  for (const key in filters) {
    const value = filters[key]
    const fieldPath = prefix ? `${prefix}.${key}` : key

    if (Array.isArray(value)) {
      // Handle array of filters for the same field
      result = result.concat(
        value.map((filter) => ({
          field: fieldPath,
          operator: filter.operator,
          value: filter.value,
        }))
      )
    } else if (value && typeof value === 'object' && !('operator' in value)) {
      // Handle nested objects (like adoptions)
      result = result.concat(flattenFilters(value, fieldPath))
    } else if (value && typeof value === 'object') {
      // Legacy single filter object - wrap in array
      result.push({
        field: fieldPath,
        operator: value.operator,
        value: value.value,
      })
    }
  }
  return result
}

const AdvancedFilterView = () => {
  const basicFilters = useAppStore.use.basicFilters()
  const setBasicFilters = useAppStore.use.setBasicFilters()

  const advancedFilters = useAppStore.use.advancedFilters()
  const setAdvancedFilters = useAppStore.use.setAdvancedFilters()
  const resetAdvancedFilters = useAppStore.use.resetAdvancedFilters()

  const defaultFormValues = useMemo(() => {
    if (!advancedFilters.filters) {
      return {
        filters: [
          {
            field: '',
            operator: '',
            value: '',
          },
        ],
      }
    }

    return {
      filters: flattenFilters(advancedFilters.filters),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDeepCompareMemoize(advancedFilters.filters)])

  const form = useForm<AdvancedFilterFormValues>({
    mode: 'onBlur',
    defaultValues: defaultFormValues,
  })

  const { control, formState, setValue, reset } = form

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'filters',
  })

  const resetFormDefault = useCallback(() => {
    reset({
      filters: [],
    })

    for (const [index, _] of fields.entries()) {
      remove(index)
    }
  }, [fields, remove, reset])

  useEffect(() => {
    if (fields.length === 0) {
      resetFormDefault()
      resetAdvancedFilters()

      append({ field: '', operator: '', value: '' })
    }
  }, [fields.length, resetFormDefault, resetAdvancedFilters, append])

  const onSubmit = (data: AdvancedFilterFormValues) => {
    const filtersObj = {} as {
      [key: string]: any
    }

    // Group filters by their field path
    const groupedFilters = data.filters.reduce(
      (acc, f) => {
        const fieldPath = f.field.split('.')
        const fieldKey = fieldPath.join('.')
        if (!acc[fieldKey]) {
          acc[fieldKey] = []
        }
        acc[fieldKey].push({
          operator: f.operator,
          value: f.value,
          type: AdvancedFilterSchema.find((fs) => fs.field === f.field)
            ?.type as 'string' | 'boolean' | 'date' | 'enum',
        })
        return acc
      },
      {} as Record<string, any[]>
    )

    // Set each group in the filters object
    Object.entries(groupedFilters).forEach(([fieldPath, filters]) => {
      const pathParts = fieldPath.split('.')
      setNestedValue(filtersObj, pathParts, filters)
    })

    setAdvancedFilters({
      ...advancedFilters,
      filters: filtersObj,
      isDirty: true,
    })
  }

  useAutoSave({
    defaultValues: defaultFormValues,
    form,
    interval: 100,
    onSave: onSubmit,
  })

  return (
    <BottomSheetScrollView contentContainerStyle={styles.scrollContent}>
      <View style={styles.paddedContent}>
        <View style={styles.resetButtonRow}>
          <UniButton
            disabled={!advancedFilters.isDirty}
            icon="restore"
            onPress={() => {
              resetAdvancedFilters()
              resetFormDefault()
            }}
          >
            Reset Filters
          </UniButton>
          <TourSpot id="foster-grid-filter-advanced-basic">
            <UniButton
              icon="filter-outline"
              onPress={() => {
                setBasicFilters({
                  ...basicFilters,
                  isActive: true,
                })
                setAdvancedFilters({
                  ...advancedFilters,
                  isActive: false,
                })
              }}
            >
              Basic Filters
            </UniButton>
          </TourSpot>
        </View>
        {fields.map((field, index) => (
          <View key={`${field.id}-view`}>
            <AdvancedFilterRow
              control={control}
              errors={formState.errors}
              fields={AdvancedFilterSchema}
              id={field.id}
              index={index}
              key={field.id}
              remove={remove}
              setValue={setValue}
            />
            {index !== fields.length - 1 && (
              <UniDivider
                bold={true}
                style={{
                  marginVertical: 20,
                }}
              />
            )}
          </View>
        ))}
        <TourSpot id="foster-grid-filter-advanced-add">
          <View style={styles.buttonContainer}>
            <UniButton
              mode="contained-tonal"
              onPress={() => append({ field: '', operator: '', value: '' })}
              style={{ width: '100%', flex: 1 }}
            >
              <Text>Add Another Filter</Text>
            </UniButton>
          </View>
        </TourSpot>
      </View>
    </BottomSheetScrollView>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    buttonContainer: {
      marginTop: theme.tokens.spacing[4],
      paddingBottom: theme.tokens.spacing[4],
    },
    resetButtonRow: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: theme.tokens.spacing[4],
    },
    paddedContent: {
      backgroundColor: theme.colors.surface,
      paddingBottom: theme.tokens.spacing[6],
      paddingHorizontal: theme.tokens.spacing[6],
    },
    scrollContent: {
      flexGrow: 1,
    },
  }
})

export default AdvancedFilterView
